/**
 * Capability Registry — governance for feature flags and tenant capabilities.
 *
 * Rules:
 * - Every capability must have an owner and intended retirement date
 * - Nav items that reference capabilities must match entries here
 * - CI gate rejects capabilities without owner/sunset
 */

export interface CapabilityEntry {
  /** Unique capability key (matches navConfig capabilities and ClinicFeatureGuard flags) */
  name: string;
  /** Description of what this capability controls */
  description: string;
  /** Team or individual responsible */
  owner: string;
  /** When this capability was introduced (ISO date) */
  created_at: string;
  /** Target date for removal — capabilities should not be permanent (ISO date) */
  intended_retirement_date: string;
}

export const capabilityRegistry: CapabilityEntry[] = [
  {
    name: 'clinic_sor_schema',
    description: 'Clinic System of Record schema — gates Encounters, Staff, and advanced clinic features',
    owner: 'clinic-team',
    created_at: '2026-01-15',
    intended_retirement_date: '2026-06-30',
  },
  {
    name: 'auto_muster_on_emergency_visit',
    description: 'Auto-muster trigger on emergency clinic visits',
    owner: 'clinic-team',
    created_at: '2026-02-01',
    intended_retirement_date: '2026-06-30',
  },
  {
    name: 'module_asset_active',
    description: 'Asset Management module — gates asset registry, schedules, activity records, and cross-module links',
    owner: 'asset-team',
    created_at: '2026-04-06',
    intended_retirement_date: '2027-06-30',
  },
  {
    name: 'module_workflow_active',
    description:
      'Workflow Studio module — gates the workflow definition authoring UI (7-Q) and all subsequent Slice-F frontend slices (7-R / 7-S / 7-T). Flipped off by default; flipped on per-tenant during rollout.',
    owner: 'workflow-studio-lane',
    created_at: '2026-04-18',
    intended_retirement_date: '2026-10-31',
  },
];

/** Set of all registered capability names for fast lookup */
export const registeredCapabilities = new Set(
  capabilityRegistry.map((c) => c.name),
);

/** Validate that a capability name is registered */
export function isRegisteredCapability(name: string): boolean {
  return registeredCapabilities.has(name);
}
